import React from 'react';
import { Box } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { preprocessMathDelimiters, remarkPlugins, rehypePlugins } from './markdownUtils';

/**
 * Lightweight markdown + math renderer shared by surfaces that just need
 * readable prose (e.g. the admin Conversation Viewer) rather than the full
 * rich-message treatment in ChatMessage (file cards, code copy buttons,
 * document links, etc.).
 */
const MarkdownContent = ({ content, sx, componentsOverride }) => {
  if (!content) return null;

  return (
    <Box
      sx={{
        fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        "& p": { margin: 0, mb: 1 },
        "& p:last-child": { mb: 0 },
        "& ul, & ol": { pl: 2.5, mb: 1 },
        "& li": { mb: 0.25 },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          mb: 1,
          mt: 1.5,
          "&:first-of-type": { mt: 0 },
          color: "inherit"
        },
        "& h1": { fontSize: "1.15rem", fontWeight: 700 },
        "& h2": { fontSize: "1.1rem", fontWeight: 700 },
        "& h3": { fontSize: "1rem", fontWeight: 650 },
        "& h4, & h5, & h6": { fontSize: "0.95rem", fontWeight: 600 },
        "& code": {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          backgroundColor: "rgba(0,0,0,0.05)",
          borderRadius: "4px",
          px: 0.5,
          py: 0.1,
          fontSize: "0.9em"
        },
        "& pre": {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          backgroundColor: "rgba(0,0,0,0.05)",
          borderRadius: "6px",
          p: 1.5,
          overflow: "auto",
          "& code": { backgroundColor: "transparent", p: 0 }
        },
        "& blockquote": {
          borderLeft: "3px solid rgba(0,0,0,0.15)",
          pl: 1.5,
          ml: 0,
          fontStyle: "italic",
          color: "inherit",
          opacity: 0.85
        },
        // Same overflow-safe treatment as ChatMessage's markdown block -
        // see the comment there for why this lives on the <table> itself.
        "& table": {
          display: "block",
          width: "max-content",
          maxWidth: "100%",
          overflowX: "auto",
          borderCollapse: "collapse",
          mb: 1,
          fontSize: "0.92em"
        },
        "& th, & td": {
          border: "1px solid rgba(0,0,0,0.12)",
          px: 1.25,
          py: 0.5,
          textAlign: "left"
        },
        "& th": {
          fontWeight: 600,
          backgroundColor: "rgba(0,0,0,0.03)"
        },
        ...sx
      }}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={componentsOverride}
      >
        {preprocessMathDelimiters(content)}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownContent;
